import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Message } from "primereact/message";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { ChecklistReviewRow } from "../../types/reviewAnswer";
import { ReviewTableHeader } from "./ReviewTableHeader";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import {
    selectAnswer,
    selectSurveyorRows,
    selectSurveyorLoading,
    selectSurveyorError,
} from "../../store/selectors/answerSelectors";
import { selectChecklistLoading } from "../../store/selectors/checklistSelectors";
import { setFilters, setPage, setSort } from "../../store/slices/answerSlice";
import { reviewAnswerColumns, reviewGlobalFilterFields } from "../../utils/reviewTableHelper";

type Props = {
    /** ISO timestamp of the surveyor's submission, if known. No fallback to
     *  other timestamps — a wrong "submitted" claim is worse than none. */
    submittedAt: string | null;
};

/**
 * Read-only view of the surveyor's original submission (A2-9094). Mirrors the
 * Checklist Review table format but is fed from the answers table. Filter,
 * sort and page state is shared with the editable tab (same Redux state), so
 * switching tabs compares the same slice of both versions.
 */
export const SurveyorSubmissionTable = ({ submittedAt }: Props) => {
    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();
    const { filters, sort, page } = useAppSelector(selectAnswer);
    const rows = useAppSelector(selectSurveyorRows);
    const loading = useAppSelector(selectSurveyorLoading);
    const error = useAppSelector(selectSurveyorError);
    const checklistLoading = useAppSelector(selectChecklistLoading);

    const onFilter = useCallback((event: DataTableStateEvent) => {
        dispatch(setFilters(event.filters));
    }, [dispatch]);

    const onSort = useCallback((event: DataTableStateEvent) => {
        dispatch(setSort({ sortField: event.sortField, sortOrder: event.sortOrder }));
    }, [dispatch]);

    const onPage = useCallback((event: DataTableStateEvent) => {
        dispatch(setPage({ row: event.rows, first: event.first }));
    }, [dispatch]);

    // The header count must reflect this table's own filtered rows — responses
    // differ between versions, so filters can match differently per tab.
    const [filteredCount, setFilteredCount] = useState<number | null>(null);
    const onValueChange = useCallback((value: ChecklistReviewRow[]) => {
        setFilteredCount(value.length);
    }, []);

    // Highlight unanswered questions
    const rowClassName = useCallback(
        (row: ChecklistReviewRow) => (row.response === "" ? "unanswered-question" : ""),
        []
    );

    const columns = useMemo(() => reviewAnswerColumns(t), [t]);
    const rowOptions = useMemo(() => [50, 75, 100, 150, 250], []);

    const submittedDate = submittedAt ? new Date(submittedAt) : null;
    const submittedLabel = submittedDate && !Number.isNaN(submittedDate.getTime())
        ? t("answers.surveyorSubmission.submitted", {
            date: submittedDate.toLocaleDateString("en-GB"),
            time: submittedDate.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
        })
        : t("answers.surveyorSubmission.submittedUnknown");

    if (error) {
        return (
            <div className="flex align-items-center justify-content-center p-5">
                <p>{t("answers.surveyorSubmission.loadError")}</p>
            </div>
        );
    }

    return (
        <>
            <Message
                severity="success"
                className="w-full mb-3 justify-content-center surveyor-readonly-banner"
                content={
                    <span className="flex align-items-center gap-2">
                        <i className="pi pi-lock readonly-banner-icon" />
                        <span className="readonly-banner-label">
                            {t("answers.surveyorSubmission.readOnly")}
                        </span>
                        <span className="readonly-banner-detail">
                            {" — "}
                            {submittedLabel}
                        </span>
                    </span>
                }
            />
            <ReviewTableHeader filteredCount={filteredCount ?? rows.length} totalCount={rows.length} />
            <DataTable
                value={rows}
                dataKey="questionId"
                loading={loading || checklistLoading}
                filters={filters}
                globalFilterFields={reviewGlobalFilterFields}
                onFilter={onFilter}
                sortField={sort.sortField}
                sortOrder={sort.sortOrder}
                onSort={onSort}
                onValueChange={onValueChange}
                filterDisplay="row"
                paginator
                scrollable
                removableSort
                stripedRows
                size="small"
                first={page.first}
                rows={page.row}
                rowsPerPageOptions={rowOptions}
                onPage={onPage}
                rowClassName={rowClassName}
            >
                {columns}
            </DataTable>
        </>
    );
};
